/* Copyright (c) 2025 MOCA Systems, Inc.  All rights reserved. */
import { Component } from '@appCanvasCommon/component-system/Component';
import { ComponentInterface } from '@appCanvasCommon/interfaces/component.interface';
import { CanvasAngularLand } from '../../../../app-data/interfaces/canvas-angular-land';
import { AbstractApplication } from '@appCanvasCommon/core/AbstractApplication';
import { Rectangle } from 'pixi.js-legacy';
import { ExportCapture, ICapturedTile, ITileOffset } from '@appCanvasCommon/export/ExportCapture';
import { HighResolutionText } from '@appCanvasCommon/text/HighResolutionText';
import { FancyDefaultStyles } from '@appCanvasCommon/text/FancyDefaultStyles';
import { IPrintConfiguration } from '../../../../app-data/constants/plan-print.model';
import { Lightbox } from '@appCanvasCommon/export/Lightbox';
import { IExportRect } from '@appPlanApplication/export/FancyPrint';

/**
 * Static settings object for the project name and plan name export
 */
const settings = {
	textColor: '#000000',
	textSize: 48,
	wordWrap: false,
	xOffset: 0,
	yOffset: -48,
};
/**
 * ProjectAndPlanLabelDrawer generates a text object in the format of `<projectName> - <planName>` and then renders
 * itself to a RenderTexture. Because we are using the ExportCapture class, we do not need to add the label to our scene graph
 * before capturing the text render, and the ExportCapture automatically turns it into a ICapturedTile for us that can be
 * consumed by the ExportStitcher.
 */
export class ProjectAndPlanLabelDrawer extends Component implements ComponentInterface {
	/**
	 * HighResolutionText object representing the text to add to the export
	 * @private
	 */
	private _label: HighResolutionText = new HighResolutionText(``, FancyDefaultStyles.create());
	/**
	 * Instance of the ExportCapture. Since we are rendering the text to a RenderTexture, we do not need to add our
	 * HighResolutionText DisplayObject to a scene graph first.
	 * @private
	 */
	private _capture!: ExportCapture;
	/**
	 * Invoked at the start of the stitching process. Returns a ICapturedTile that can be used by the stitcher.
	 * @param canvasAngularLand - Angular dependencies, etc
	 * @param application - The AbstractApplication this is attached to
	 * @param lightbox - The rectangle representing the export region
	 * @param config - The current Print Configuration used for the capture
	 * @param exportRect - The rectangle of the final export
	 * @returns ICapturedTile - Returns a ICapturedTile object that can be consumed by the ExportStitcher
	 */
	public onExportDrawNode(
		canvasAngularLand: CanvasAngularLand,
		application: AbstractApplication,
		lightbox: Lightbox,
		config: IPrintConfiguration,
		exportRect: IExportRect
	): ICapturedTile {
		if (this._capture) {
			this._capture.purge();
		}
		this._capture = new ExportCapture(application);
		const projectName: string | undefined = canvasAngularLand.projectObject.syncObject.projectName;
		const planName: string | undefined = canvasAngularLand.plan.syncObject.view.name;
		this._label.style.fill = settings.textColor;
		this._label.style.wordWrap = settings.wordWrap;
		this._label.style.fontSize = settings.textSize;
		this._label.text = `${projectName} - ${planName}`;
		const offset: ITileOffset = {
			x: settings.xOffset,
			y: settings.yOffset,
		};
		return this._capture.capture(
			new Rectangle(0, 0, this._label.width, this._label.height),
			this._label,
			offset
		);
	}

	/**
	 * Invoked once the export process is completed.
	 * @param canvasAngularLand
	 * @param application
	 */
	public onExportComplete(canvasAngularLand: CanvasAngularLand, application: AbstractApplication): void {
		this._capture.purge();
		this._label.text = ``;
	}
}
